/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';
import { Text } from '@/shared/ui';
import { buildSearchSummary } from '@/shared/utils/partnerships-search-record';
import type { PartnershipResult } from '../context/partnerships-wizard-types';
import {
  advertiserBreakdown,
  presentAdvertiserBuckets,
} from '../utils/results-grouping';

interface SearchSummaryStatsProps {
  propertiesRetrieved?: number | null;
  /**
   * The search's results, used to compute the real advertiser-type breakdown
   * (owner / agency / broker / other) shown beside the headline count. Only
   * consumed in headline (non-compact) mode.
   */
  results?: PartnershipResult[];
  /** Compact inline layout for list rows; false renders the headline summary. */
  compact?: boolean;
}

/**
 * Renders a search record's results summary.
 *
 * The headline (non-compact) mode shows the one figure that matters — the
 * total properties found ("N imóveis encontrados") — followed by the real
 * advertiser-type breakdown (owner / agency / broker / other) computed from the
 * results, consistent with the compacted {@link ResultsList} summary. It never
 * surfaces an aggregate "N imobiliárias" figure, which was misleading because
 * the results are properties from mixed advertiser types, not N agencies.
 *
 * Compact mode produces a terse inline sentence for history-list rows, driven
 * by the pure {@link buildSearchSummary} helper (the only reported figure in
 * that context is the property count).
 *
 * @param props - The reported count, the results, and the layout flag.
 * @returns The summary surface, or null when there is nothing to report.
 */
export function SearchSummaryStats({
  propertiesRetrieved,
  results,
  compact = false,
}: SearchSummaryStatsProps) {
  const { t } = useTranslation('features/partnerships');

  if (compact) {
    const stats = buildSearchSummary({ properties_retrieved: propertiesRetrieved });
    if (stats.length === 0) return null;
    return (
      <Text span size="xs" className="text-[var(--text-secondary)]" data-testid="search-summary-compact">
        {stats
          .map((stat) => `${stat.value} ${t(`summary.${stat.key}`, { count: stat.value })}`)
          .join(' · ')}
      </Text>
    );
  }

  const total =
    typeof propertiesRetrieved === 'number' ? propertiesRetrieved : results?.length ?? 0;
  const buckets = presentAdvertiserBuckets(advertiserBreakdown(results ?? []));

  return (
    <div
      className="flex flex-wrap items-baseline gap-x-[var(--space-2)] gap-y-[var(--space-1)]"
      data-testid="search-summary-stats"
    >
      <Text variant="body" size="sm" fw={600}>
        {total} {t('summary.properties', { count: total })}
      </Text>
      {buckets.length > 0 && (
        <Text
          span
          className="flex flex-wrap items-baseline gap-x-[var(--space-2)] gap-y-[var(--space-1)] text-[var(--text-tertiary)]"
          data-testid="summary-advertiser-breakdown"
        >
          {buckets.map(([bucket, count]) => (
            <Text key={bucket} span size="xs" fw={500} tone="tertiary">
              · {count} {t(`results.advertiser_${bucket}`)}
            </Text>
          ))}
        </Text>
      )}
    </div>
  );
}
